import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { findService, SERVICES } from "@/data/services";
import QuoteForm from "@/components/QuoteForm";

const ServiceDetail = () => {
  const { slug } = useParams();
  const s = findService(slug);
  if (!s) return <Navigate to="/services" replace />;
  const others = SERVICES.filter((x) => x.slug !== slug);

  return (
    <div data-testid={`service-detail-${slug}`}>
      <section className="relative overflow-hidden bg-crest-navy text-white">
        <img src={s.image} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-crest-navy via-crest-navy/85 to-crest-navy/40" />
        <div className="relative mx-auto max-w-7xl px-6 py-20 md:py-28">
          <Link to="/services" className="inline-flex items-center gap-2 text-crest-gold text-xs uppercase tracking-[0.18em] hover:text-white transition-colors duration-200" data-testid="back-to-services">
            <ArrowLeft className="h-3.5 w-3.5" /> All services
          </Link>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] max-w-4xl">{s.name}</h1>
          <p className="mt-5 max-w-2xl text-white/80 font-body text-lg">{s.short}</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16 md:py-24 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-crest-navy">What we deliver</h2>
          <ul className="mt-6 space-y-3 text-base">
            {s.bullets.map((b) => (
              <li key={b} className="flex items-start gap-3 rounded-md border border-border bg-white px-5 py-4">
                <span className="mt-1.5 h-2 w-2 rounded-full bg-crest-gold shrink-0" />
                <span className="text-crest-navy">{b}</span>
              </li>
            ))}
          </ul>

          <h2 className="mt-14 font-display text-3xl md:text-4xl font-bold text-crest-navy">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="mt-6" data-testid={`service-faqs-${slug}`}>
            {s.faqs.map((f) => (
              <AccordionItem key={f.q} value={f.q}>
                <AccordionTrigger className="text-left text-base font-display text-crest-navy">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground font-body leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <aside className="lg:col-span-5 lg:sticky lg:top-28 self-start">
          <QuoteForm title="Get an estimate" subtitle={`Tell us about your ${s.name.toLowerCase()} project.`} />
        </aside>
      </section>

      <section className="bg-crest-surface border-t border-border">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <p className="text-xs uppercase tracking-[0.22em] text-crest-gold font-medium">Other services</p>
          <h3 className="mt-3 font-display text-2xl md:text-3xl font-bold text-crest-navy">Explore the rest of what we do</h3>
          <div className="mt-8 grid md:grid-cols-3 gap-5">
            {others.map((o) => (
              <Link
                key={o.slug}
                to={`/services/${o.slug}`}
                className="group rounded-lg overflow-hidden border border-border bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                data-testid={`other-service-${o.slug}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={o.image} alt={o.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <div className="font-display text-base font-semibold text-crest-navy">{o.name}</div>
                  <div className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.16em] text-crest-green">
                    Open <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
